import { fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import type { BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { Mutex } from 'async-mutex';
import { clearTokenItem, getTokenItem, setTokenItem, TOKEN_KEY } from '../utils/helpers';

const BASE_URL = process.env.REACT_APP_BASE_URL as string;
const API_KEY = process.env.REACT_APP_API_SECRET_KEY as string;

const mutex = new Mutex();

const baseQuery = fetchBaseQuery({
  baseUrl: BASE_URL,
  timeout: 100000,
  prepareHeaders: (headers) => {
    const token = getTokenItem(TOKEN_KEY.AUTH_TOKEN);
    if (token) {
      headers.set('authorization', `Bearer ${token}`);
    }
    // console.log(`API KEY SET ${API_KEY}`)
    headers.set('x-api-key', API_KEY);
    return headers;
  },
});

export const baseQueryWithReauth: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (args, api, extraOptions) => {
  await mutex.waitForUnlock();
  let result = await baseQuery(args, api, extraOptions);

  if (result.error && result.error.status === 401) {
    if (!mutex.isLocked()) {
      const release = await mutex.acquire();

      try {
        const refreshToken = getTokenItem(TOKEN_KEY.REFRESH_TOKEN);

        if (refreshToken) {
          const refreshQuery = fetchBaseQuery({
            baseUrl: BASE_URL,
            prepareHeaders: (headers) => {
              headers.set('authorization', `Bearer ${refreshToken}`);
              headers.set('x-api-key', API_KEY);
              return headers;
            },
          });

          const refreshResult = await refreshQuery(
            {
              url: '/auth/refresh-token',
              method: 'POST',
            },
            api,
            extraOptions,
          );

          if (refreshResult.data) {
            const { access_token } = refreshResult.data as { access_token: string };
            setTokenItem(TOKEN_KEY.AUTH_TOKEN, access_token);
            result = await baseQuery(args, api, extraOptions);
          } else {
            clearTokenItem(TOKEN_KEY.AUTH_TOKEN);
          }
        } else {
          clearTokenItem(TOKEN_KEY.AUTH_TOKEN);
        }
      } finally {
        release();
      }
    } else {
      await mutex.waitForUnlock();
      result = await baseQuery(args, api, extraOptions);
    }
  }

  return result;
};
